import { notifications } from '@mantine/notifications';
import { getChannel, Channel } from '@ayocore/platform/messaging/client';
import { AuthController } from '@ayocore/platform/identity/client/AuthController';
import type { AuthControllerState } from '@ayocore/platform/identity/client/AuthController';
import {
  NotificationMessageSchema,
  type NotificationMessage,
  type NotificationMarker,
} from '../common/types';
import {
  useNotificationRepository,
  getVapidPublicKey as getVapidPublicKeyRpc,
} from '@ayocore/platform/notifications/client/rpc';
import { WebPushController } from './services/WebPushController';
import { isStaleNotificationMessage, pickAppliedState } from './realtime';
import type { Notification } from '../schemas/notification.schema';
import logger from '@ayocore/platform/common/helpers/logger';
import { ensureServiceWorkerRegistration } from '@ayocore/platform/client/pwa/registerServiceWorker';

/**
 * NotificationController - Clean Singleton for Global Notifications
 *
 * Follows the same pattern AuthController:
 * - External state management (not React state)
 * - Simple callback subscription system
 * - Self-initializing with auth state monitoring
 * - Always-on WebSocket connection when authenticated
 * - Push notification management
 */

/** Поля уведомления, которые приходят в реальном времени; остальные — из состояния */
type NotificationRealtimeFields = Pick<
  Notification,
  | 'id'
  | 'title'
  | 'message'
  | 'type'
  | 'user_id'
  | 'is_read'
  | 'created_at'
  | 'updated_at'
  | 'entity_url'
  | 'entity_type'
  | 'payload'
  | 'group_count'
  | 'last_event_at'
  | 'dedup_key'
>;

interface NotificationState {
  notifications: Notification[];
  /**
   * Маркеры всех непрочитанных — сводка без текстов, источник счётчиков разделов.
   * Отдельно от `notifications`: лента ограничена 50 последними строками,
   * и считать бейджи по ней значило бы занижать их.
   */
  markers: NotificationMarker[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  // Push notification state
  pushEnabled: boolean;
  pushPermission: NotificationPermission;
  pushSupported: boolean;
  pushLoading: boolean;
}

const notificationRepository = useNotificationRepository();

export class NotificationController {
  private static instance: NotificationController;

  // External state (not React state)
  private state: NotificationState = {
    notifications: [],
    markers: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    // Push notification state
    pushEnabled: false,
    pushPermission: 'default',
    pushSupported: false,
    pushLoading: false,
  };

  // Simple callback system (no RxJS)
  private listeners: Set<() => void> = new Set();
  private authController = AuthController.getInstance();
  private channel: Channel<unknown, NotificationMessage> | null = null;
  private pushController = WebPushController.getInstance();

  private constructor() {
    // Initialize push state
    this.initializePushState();

    // Убеждаемся, что Service Worker уже зарегистрирован (инициализация PWA)
    void ensureServiceWorkerRegistration();

    // Subscribe to auth changes - setup/cleanup WebSocket
    this.authController.subscribe(() => {
      const authState = this.authController.getSnapshot();
      this.handleAuthChange(authState);
    });

    // Initialize immediately if already authenticated
    const currentAuth = this.authController.getSnapshot();
    if (currentAuth.isAuthenticated && currentAuth.user) {
      this.setupWebSocket();
      this.loadNotifications();
    }
  }

  static getInstance(): NotificationController {
    if (!NotificationController.instance) {
      NotificationController.instance = new NotificationController();
    }
    return NotificationController.instance;
  }

  // Subscription system for React integration
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // Get current state snapshot
  // Важно: useSyncExternalStore требует референциальной стабильности снапшота,
  // поэтому возвращаем тот же объект состояния без копирования.
  getSnapshot = (): NotificationState => this.state;

  // Notify all listeners of state changes
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        logger.error('Error in notification listener:', error);
      }
    });
  }

  // Handle auth state changes
  private handleAuthChange(authState: AuthControllerState): void {
    if (authState.isAuthenticated && authState.user) {
      this.setupWebSocket();
      this.loadNotifications();
    } else {
      this.cleanup();
    }
  }

  // Helper: immutable state replace
  private replaceState(patch: Partial<NotificationState>): void {
    this.state = { ...this.state, ...patch };
  }

  // Setup WebSocket connection (always-on when authenticated)
  private setupWebSocket(): void {
    if (this.channel) {
      return; // Already set up
    }

    try {
      this.channel = getChannel('notifications').withSchema(NotificationMessageSchema);

      // Listen for real-time notifications
      this.channel.onMessage((message: NotificationMessage) => {
        logger.info('🔔 [NotificationController] Received WebSocket message:', message);
        try {
          this.upsertNotification(message);
        } catch (error) {
          logger.error('Error processing notification message:', error);
        }
      });

      // Handle connection events
      this.channel.onConnectionStart(() => {
        this.clearError();
      });

      this.channel.onConnectionLost(() => {
        this.setError('Connection lost. Notifications may be delayed.');
      });

      this.channel.onError((error: Error) => {
        this.setError(`Notification system error: ${error.message}`);
      });
    } catch (error) {
      logger.error('Failed to setup WebSocket:', error);
      this.setError('Failed to connect to notification system');
    }
  }

  /**
   * Загрузить ленту текущего пользователя.
   *
   * Получателя и порядок сортировки определяет сервер (`getFeed`): схлопнутое
   * уведомление должно всплывать по времени последнего события, а не стоять
   * на дате первого — выразить это фильтром на клиенте нельзя.
   */
  private async loadNotifications(): Promise<void> {
    try {
      this.replaceState({ isLoading: true });
      this.clearError();
      this.notifyListeners();

      const [data, markers] = await Promise.all([
        notificationRepository.getFeed(50),
        notificationRepository.getUnreadMarkers(),
      ]);

      const notifications = Array.isArray(data) ? data : [];
      const unreadCount = notifications.filter(n => !n.is_read).length;
      this.replaceState({
        notifications,
        unreadCount,
        markers: Array.isArray(markers) ? markers : [],
      });
    } catch (error) {
      logger.error('Failed to load notifications:', error);
      this.setError('Failed to load notifications');
      this.replaceState({ notifications: [], unreadCount: 0, markers: [] });
    } finally {
      this.replaceState({ isLoading: false });
      this.notifyListeners();
    }
  }

  /**
   * Пересчитать маркеры после уведомления из реального времени.
   *
   * Прочитанное уведомление маркера не имеет: схлопнутый повтор снимает отметку
   * о прочтении, и маркер обязан вернуться — иначе счётчик раздела не заметил бы
   * нового события по объекту, который пользователь уже смотрел.
   */
  private applyMarker(message: NotificationMessage): NotificationMarker[] {
    const others = this.state.markers.filter(marker => marker.id !== message.id);

    if (message.is_read) {
      return others;
    }

    const marker: NotificationMarker = {
      id: message.id,
      dedup_key: message.dedup_key ?? null,
      entity_type: message.entity_type ?? null,
      entity_url: message.entity_url ?? null,
      group_count: message.group_count ?? 1,
    };

    return [marker, ...others];
  }

  /**
   * Поля уведомления, которые несёт WebSocket-сообщение.
   *
   * Это не полная строка: связей (`entity_uuid`, `entity_type`) и `uuid`
   * в сообщении нет — при обновлении известной строки они берутся из состояния.
   */
  private messageToPatch(message: NotificationMessage): NotificationRealtimeFields {
    return {
      id: message.id,
      title: message.title,
      message: message.message,
      type: message.type,
      user_id: message.user_id,
      is_read: message.is_read,
      created_at: new Date(message.created_at), // Convert string to Date
      updated_at: new Date(message.created_at), // Use same timestamp
      entity_url: message.entity_url ?? null,
      entity_type: message.entity_type ?? null,
      payload: message.payload ?? null,
      group_count: message.group_count ?? 1,
      last_event_at: message.last_event_at ? new Date(message.last_event_at) : null,
      // Без ключа группы строка ленты не погаснет вместе со своим маркером:
      // гашение по ключам её просто не найдёт, и общий счётчик застрянет
      dedup_key: message.dedup_key ?? null,
    };
  }

  /**
   * Положить уведомление из реального времени в состояние.
   *
   * Известный `id` означает схлопнутое уведомление (дедупликация обновляет
   * строку вместо создания новой): обновляем содержимое и поднимаем наверх —
   * повторное событие должно быть видно там же, где новое.
   */
  private upsertNotification(message: NotificationMessage): void {
    logger.info('🔔 [NotificationController] Upserting notification:', message);

    const existing = this.state.notifications.find(n => n.id === message.id);
    const appliedMarker = this.state.markers.find(marker => marker.id === message.id);

    // Отставшее сообщение откатило бы счётчик группы назад: слияния идут
    // параллельно, и порядок их доставки не гарантирован. Сверяемся с обоими
    // источниками — вытесненное из ленты уведомление живёт только в сводке,
    // и сравнение с одной лентой пропустило бы откат бейджа раздела
    if (isStaleNotificationMessage(pickAppliedState(existing, appliedMarker), message)) {
      logger.info('🔔 [NotificationController] Пропущено устаревшее сообщение:', message.id);
      return;
    }

    const patch = this.messageToPatch(message);
    const notification = existing
      ? { ...existing, ...patch }
      : {
          uuid: '', // Will be populated when loaded from DB
          entity_uuid: null,
          entity_type: null,
          user: null, // Required for relation pattern
          sort_order: null,
          push_sent: false,
          dedup_key: null,
          ...patch,
        };

    // Наверх списка, максимум 50 записей
    const others = this.state.notifications.filter(n => n.id !== message.id);
    const notifications = [notification, ...others.slice(0, 49)];
    const unreadCount = notifications.filter(n => !n.is_read).length;
    this.replaceState({ notifications, unreadCount, markers: this.applyMarker(message) });
    this.notifyListeners();

    // Показываем всплывающее окно только по непрочитанным
    if (!notification.is_read) {
      logger.info('🔔 [NotificationController] Showing popup for notification');
      this.showPopupNotification(notification);
    }
  }

  // Show popup notification with enhanced styling
  private showPopupNotification(notification: Notification): void {
    logger.info('🔔 [NotificationController] Attempting to show popup for:', notification);
    try {
      const color = this.getNotificationColor(notification.type);
      const icon = this.getNotificationIcon(notification.type);

      logger.info('🔔 [NotificationController] Calling notifications.show with:', {
        id: `notification-${notification.id}`,
        title: `🔔 ${notification.title}`,
        message: notification.message,
        color: color,
        icon: icon,
      });

      notifications.show({
        id: `notification-${notification.id}`,
        title: `🔔 ${notification.title}`,
        message: notification.message,
        color: color,
        icon: icon,
        autoClose: 8000,
        position: 'top-right',
        withCloseButton: true,
        withBorder: true,
        radius: 'md',
        style: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          animation: 'slideInRight 0.3s ease-out',
        },
      });

      logger.info('🔔 [NotificationController] Popup notification called successfully');

      // Play audio notification
      this.playNotificationSound(notification.type);
    } catch (error) {
      logger.error('Failed to show popup notification:', error);
    }
  }

  // Play notification sound based on type
  private playNotificationSound(type: string): void {
    try {
      // Create audio context for different notification types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different frequencies for different notification types
      switch (type) {
        case 'success':
          oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
          break;
        case 'warning':
          oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(500, audioContext.currentTime + 0.1);
          break;
        case 'error':
          oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);
          break;
        case 'info':
        default:
          oscillator.frequency.setValueAtTime(700, audioContext.currentTime);
          break;
      }

      // Gentle volume and duration
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      // Silently fail if audio is not supported or blocked
      logger.debug('Audio notification not available:', error);
    }
  }

  // Get notification icon based on type
  private getNotificationIcon(type: string): string {
    switch (type) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      case 'info':
      default:
        return '💡';
    }
  }

  // Set error state
  private setError(error: string): void {
    this.replaceState({ error });
    this.notifyListeners();
  }

  // Clear error state
  private clearError(): void {
    if (this.state.error) {
      this.replaceState({ error: null });
      this.notifyListeners();
    }
  }

  // Clean up when user logs out
  private cleanup(): void {
    this.replaceState({
      notifications: [],
      markers: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
    });
    this.channel = null;
    this.notifyListeners();
  }

  // Get notification color for toasts
  private getNotificationColor(type: string): string {
    switch (type) {
      case 'success':
        return 'green';
      case 'warning':
        return 'yellow';
      case 'error':
        return 'red';
      case 'info':
      default:
        return 'blue';
    }
  }

  // Public API methods

  /**
   * Get all notifications
   */
  getNotifications(): Notification[] {
    return [...this.state.notifications];
  }

  /**
   * Get unread notification count
   */
  getUnreadCount(): number {
    return this.state.unreadCount;
  }

  /**
   * Check if loading
   */
  isLoading(): boolean {
    return this.state.isLoading;
  }

  /**
   * Get current error
   */
  getError(): string | null {
    return this.state.error;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: number): Promise<void> {
    try {
      // Доменный метод, а не generic update: получателя он берёт из контекста
      await notificationRepository.markAsRead(notificationId);

      // Update local state
      const notifications = this.state.notifications.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      );
      const unreadCount = notifications.filter(n => !n.is_read).length;
      const markers = this.state.markers.filter(marker => marker.id !== notificationId);
      this.replaceState({ notifications, unreadCount, markers });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to mark notification as read:', error);
      this.setError('Failed to mark notification as read');
      throw error;
    }
  }

  /**
   * Перечитать сводку непрочитанных с сервера.
   *
   * Нужна там, где локально состояние не восстановить точно: массовые операции
   * трогают строки, которых нет в загруженной ленте.
   */
  async refreshMarkers(): Promise<void> {
    try {
      const markers = await notificationRepository.getUnreadMarkers();
      this.replaceState({ markers: Array.isArray(markers) ? markers : [] });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to refresh notification markers:', error);
    }
  }

  /**
   * Отметить прочитанными уведомления по ключам групп.
   *
   * Так гасит свои уведомления карточка объекта: ключ группы она знает сама,
   * и сводка маркеров для этого не нужна. Пустой список — сервер не тревожим.
   */
  async markReadByDedupKeys(dedupKeys: string[]): Promise<void> {
    if (dedupKeys.length === 0) return;

    try {
      await notificationRepository.markAsReadByDedupKeys(dedupKeys);

      const keys = new Set(dedupKeys);
      this.applyReadLocally(
        notification => Boolean(notification.dedup_key && keys.has(notification.dedup_key)),
        marker => Boolean(marker.dedup_key && keys.has(marker.dedup_key))
      );
    } catch (error) {
      logger.error('Failed to mark notifications as read by dedup keys:', error);
      this.setError('Failed to mark notifications as read');
    }
  }

  /**
   * Отметить прочитанными уведомления по идентификаторам.
   *
   * Нужен областям, которые узнают свои уведомления не по ключу группы,
   * а по `entity_type`/`entity_url` — гасить такие по ключу нечем.
   */
  async markReadByIds(ids: number[]): Promise<void> {
    if (ids.length === 0) return;

    try {
      await notificationRepository.markAsReadByIds(ids);

      const idSet = new Set(ids);
      this.applyReadLocally(
        notification => idSet.has(notification.id),
        marker => idSet.has(marker.id)
      );
    } catch (error) {
      logger.error('Failed to mark notifications as read by ids:', error);
      this.setError('Failed to mark notifications as read');
    }
  }

  /**
   * Отразить прочтение в локальном состоянии: строки ленты гаснут, их маркеры
   * уходят из сводки. Так общий счётчик и счётчики разделов меняются вместе,
   * не дожидаясь перезагрузки.
   */
  private applyReadLocally(
    isReadNotification: (notification: Notification) => boolean,
    isReadMarker: (marker: NotificationMarker) => boolean
  ): void {
    const notifications = this.state.notifications.map(n =>
      isReadNotification(n) ? { ...n, is_read: true } : n
    );
    const unreadCount = notifications.filter(n => !n.is_read).length;
    const markers = this.state.markers.filter(marker => !isReadMarker(marker));

    this.replaceState({ notifications, unreadCount, markers });
    this.notifyListeners();
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId: number): Promise<void> {
    try {
      await notificationRepository.deleteById(notificationId);

      // Update local state
      const notifications = this.state.notifications.filter(n => n.id !== notificationId);
      const unreadCount = notifications.filter(n => !n.is_read).length;
      const markers = this.state.markers.filter(marker => marker.id !== notificationId);
      this.replaceState({ notifications, unreadCount, markers });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to delete notification:', error);
      this.setError('Failed to delete notification');
      throw error;
    }
  }

  /**
   * Refresh notifications from server
   */
  async refreshNotifications(): Promise<void> {
    const authState = this.authController.getSnapshot();
    if (authState.isAuthenticated && authState.user) {
      await this.loadNotifications();
    }
  }

  /**
   * Отметить прочитанными все уведомления пользователя.
   *
   * Гасит их на сервере целиком, а не перебором загруженных строк: непрочитанных
   * бывает больше, чем помещается в ленту, и обход по её содержимому оставлял бы
   * остальные висеть — вместе со счётчиками разделов.
   */
  async markAllAsRead(): Promise<void> {
    try {
      await notificationRepository.markAllAsReadForCurrentUser();

      // Update local state
      const notifications = this.state.notifications.map(n => ({ ...n, is_read: true }));
      this.replaceState({ notifications, unreadCount: 0, markers: [] });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to mark all notifications as read:', error);
      this.setError('Failed to mark all notifications as read');
      throw error;
    }
  }

  /**
   * Delete all notifications
   */
  async deleteAllNotifications(): Promise<void> {
    try {
      await notificationRepository.deleteAll();

      // Update local state
      this.replaceState({ notifications: [], unreadCount: 0, markers: [] });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to delete all notifications:', error);
      this.setError('Failed to delete all notifications');
      throw error;
    }
  }

  // ===== PUSH NOTIFICATION METHODS =====

  /**
   * Initialize push notification state
   */
  private initializePushState(): void {
    const supported = this.pushController.isSupported();
    const permission = this.pushController.getPermission();

    this.replaceState({
      pushSupported: supported,
      pushPermission: permission,
      pushEnabled: supported && permission === 'granted',
    });

    logger.info(
      `🔔 [NotificationController] Push state initialized: supported=${supported}, permission=${permission}`
    );
  }

  /**
   * Enable push notifications
   */
  async enablePush(): Promise<boolean> {
    if (!this.state.pushSupported) {
      logger.warn('⚠️ [NotificationController] Push notifications not supported');
      return false;
    }

    try {
      this.replaceState({ pushLoading: true });
      this.notifyListeners();

      // Get VAPID public key from server
      const vapidPublicKey = await this.getVapidPublicKey();
      if (!vapidPublicKey) {
        logger.error('❌ [NotificationController] VAPID public key not available');
        this.replaceState({ pushLoading: false });
        this.notifyListeners();
        return false;
      }

      // Enable push through WebPushController
      const success = await this.pushController.enablePush(vapidPublicKey);

      if (success) {
        this.replaceState({
          pushEnabled: true,
          pushPermission: 'granted',
          pushLoading: false,
        });
        logger.info('✅ [NotificationController] Push notifications enabled');
      } else {
        this.replaceState({ pushLoading: false });
        logger.warn('⚠️ [NotificationController] Failed to enable push notifications');
      }

      this.notifyListeners();
      return success;
    } catch (error) {
      logger.error('❌ [NotificationController] Error enabling push:', error);
      this.replaceState({ pushLoading: false });
      this.notifyListeners();
      return false;
    }
  }

  /**
   * Disable push notifications
   */
  async disablePush(): Promise<boolean> {
    try {
      this.replaceState({ pushLoading: true });
      this.notifyListeners();

      const success = await this.pushController.disablePush();

      if (success) {
        this.replaceState({
          pushEnabled: false,
          pushLoading: false,
        });
        logger.info('✅ [NotificationController] Push notifications disabled');
      } else {
        this.replaceState({ pushLoading: false });
        logger.warn('⚠️ [NotificationController] Failed to disable push notifications');
      }

      this.notifyListeners();
      return success;
    } catch (error) {
      logger.error('❌ [NotificationController] Error disabling push:', error);
      this.replaceState({ pushLoading: false });
      this.notifyListeners();
      return false;
    }
  }

  /**
   * Get push notification status
   */
  async getPushStatus(): Promise<{
    supported: boolean;
    enabled: boolean;
    permission: NotificationPermission;
    loading: boolean;
  }> {
    return {
      supported: this.state.pushSupported,
      enabled: this.state.pushEnabled,
      permission: this.state.pushPermission,
      loading: this.state.pushLoading,
    };
  }

  /**
   * Send test push notification
   */
  async sendTestPush(): Promise<boolean> {
    try {
      const success = await this.pushController.sendTestPush();

      // Оставляем только одно место логирования об успехе/ошибке
      logger.info(
        `🔔 [NotificationController] Test push result: ${success ? 'success' : 'failed'}`
      );

      return success;
    } catch (error) {
      logger.error('❌ [NotificationController] Error sending test push:', error);
      return false;
    }
  }

  /**
   * Получает публичный ключ VAPID с сервера через RPC (notifications::getVapidPublicKey).
   */
  private async getVapidPublicKey(): Promise<string | null> {
    try {
      const publicKey = await getVapidPublicKeyRpc();
      if (!publicKey) {
        logger.warn('⚠️ [NotificationController] VAPID public key not available from server');
        return null;
      }
      logger.info('✅ [NotificationController] VAPID public key retrieved from server');
      return publicKey;
    } catch (error) {
      logger.error('❌ [NotificationController] Failed to get VAPID public key:', error);
      return null;
    }
  }

  /**
   * Check push permission status
   */
  async checkPushPermission(): Promise<void> {
    const permission = this.pushController.getPermission();
    const subscription = await this.pushController.getSubscription();
    const enabled = permission === 'granted' && this.state.pushSupported && subscription !== null;

    this.replaceState({
      pushPermission: permission,
      pushEnabled: enabled,
    });

    this.notifyListeners();
  }
}
